import React, { useState } from "react";
import styled from "styled-components";
import { Mail, MailOpen, Clock, Trash2 } from "lucide-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useGetMessages, useMarkMessageRead, useDeleteMessage } from "../../services/api/contacts-api";
import Loading from "../../components/loading";

const Container = styled.div`
  padding: 2rem;
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.h1`
  font-size: 1.8rem;
  color: var(--text-main);
  margin: 0 0 1.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  h3 { margin-bottom: 0.5rem; }
`;

const MessageCard = styled.div`
  background: var(--bg-card);
  border: 1px solid ${(p) => (p.$unread ? "var(--primary-color, #6366f1)" : "var(--border-color)")};
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const MessageActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Sender = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-main);
`;

const DateLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const Email = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
`;

const Body = styled.div`
  font-size: 0.95rem;
  color: var(--text-main);
  line-height: 1.5;
  white-space: pre-wrap;
`;

const Messages = () => {
  const { data, isLoading, error } = useGetMessages();
  const { mutate: markRead } = useMarkMessageRead();
  const { mutate: deleteMessage, isPending: isDeleting } = useDeleteMessage();
  const [messageToDelete, setMessageToDelete] = useState(null);

  const messages = data?.results ?? data ?? [];

  const handleClick = (msg) => {
    if (!msg.read) markRead(msg.id);
  };

  const handleDelete = () => {
    deleteMessage(messageToDelete.id, {
      onSuccess: () => setMessageToDelete(null),
    });
  };

  if (isLoading) return <Loading text="Loading messages" />;
  if (error) return <Container><p>Error loading messages.</p></Container>;

  return (
    <Container>
      <Header>Messages</Header>
      {messages.length === 0 ? (
        <EmptyState>
          <Mail size={48} strokeWidth={1.2} />
          <h3>No messages yet</h3>
          <p>When visitors contact you through your portfolio, their messages will appear here.</p>
        </EmptyState>
      ) : (
        messages.map((msg) => (
          <MessageCard key={msg.id} $unread={!msg.read} onClick={() => handleClick(msg)}>
            <MessageHeader>
              <Sender>
                {msg.read ? <MailOpen size={18} /> : <Mail size={18} />}
                {msg.name}
              </Sender>
              <MessageActions onClick={(event) => event.stopPropagation()}>
                <DateLabel>
                  <Clock size={14} />
                  {new Date(msg.created_at).toLocaleDateString()}
                </DateLabel>
                <Button
                  icon={<Trash2 size={16} />}
                  severity="danger"
                  text
                  rounded
                  aria-label="Delete message"
                  onClick={() => setMessageToDelete(msg)}
                />
              </MessageActions>
            </MessageHeader>
            <Email>{msg.email}</Email>
            <Body>{msg.message}</Body>
          </MessageCard>
        ))
      )}
      <Dialog
        visible={!!messageToDelete}
        onHide={() => setMessageToDelete(null)}
        header="Delete message"
        modal
        style={{ width: "min(400px, 90vw)" }}
      >
        <p>Are you sure you want to delete this message? This action cannot be undone.</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
          <Button label="Cancel" text onClick={() => setMessageToDelete(null)} />
          <Button label="Delete" severity="danger" loading={isDeleting} onClick={handleDelete} />
        </div>
      </Dialog>
    </Container>
  );
};

export default Messages;
